<script lang="ts" setup>
import BasicButton from '~/components/form-elements/BasicButton.vue'
import { useBodyOverflowControl } from '~/composables/use-body-overflow-control'
import constants from '~/constants'
import helpers from '~/helpers'
import { useSearchboosterStore } from '~/store/searchbooster'

const props = withDefaults(
  defineProps<{
    isBlackoutMode: boolean
    isButton?: boolean
    title: string
  }>(),
  { isButton: false }
)

const isPopupVisible = ref(false)

const bodyOverflowControl = useBodyOverflowControl()
const searchboosterStore = useSearchboosterStore()
const { isSearchLoading } = storeToRefs(searchboosterStore)

const totalProducts = computed<number>(() => searchboosterStore.searchMeta?.hits || 0)

function closePopup (): void {
  isPopupVisible.value = false

  bodyOverflowControl.enable()
}

function openPopup (): void {
  isPopupVisible.value = true

  bodyOverflowControl.disable()
}

defineExpose({ closePopup, openPopup })
</script>

<template>
  <transition name="slide-up">
    <div v-if="isPopupVisible" class="pointer-events-none fixed inset-0 z-[9999] size-full">
      <transition name="fade">
        <div
          class="pointer-events-auto absolute inset-0"
          :class="{ 'bg-[#434242] opacity-[.4]': isBlackoutMode }"
          @click="closePopup"
        />
      </transition>
      <div
        class="
          pointer-events-auto fixed inset-x-0 bottom-0 h-auto
          w-full rounded-t-xl bg-white px-2 py-6 transition-all
          duration-300 ease-out
        "
        @click.self="closePopup"
      >
        <div class="flex items-center justify-between">
          <p class="text-sm font-medium leading-[1.43] tracking-[-0.01em] text-main-dark">
            {{ title }}
          </p>

          <a class="mr-[0.55rem] block !text-main-dark !no-underline" href="#" @click.prevent="closePopup">
            <SvgIcon class="!size-6" name="outlined/close" />
          </a>
        </div>

        <div>
          <slot />
        </div>

        <BasicButton
          v-if="isButton"
          class="m-auto mt-3 !h-14 !w-auto !rounded-2xl !text-base"
          color="green"
          :is-loading="isSearchLoading"
          is-small
          :title="`Показать ${totalProducts} ${helpers.getDeclension(totalProducts, constants.wordForms.product)}`"
          @click="closePopup"
        />
      </div>
    </div>
  </transition>
</template>

<style scoped>
@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.slide-up-enter-active {
  animation: slideUp 0.3s ease-out forwards;
}

.slide-up-leave-active {
  animation: slideUp 0.3s ease-out reverse forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

.fade-enter-active {
  animation: fadeIn 0.1s ease-in forwards;
}

.fade-leave-active {
  animation: fadeOut 0.1s ease-out forwards;
}
</style>
